import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import { NotesIcon } from '../../components/notes/NotesIcon';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import { 
  Building, Plus, Edit, Trash2, Search, RefreshCw, Gauge, Zap, Flame, Droplet, 
  Link, PlusCircle, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle,
  Eye, Key, CreditCard, ExternalLink, X, Wind, Sun, Snowflake, Loader2
} from 'lucide-react';

const VendorAccounts = () => {
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [utilityAccounts, setUtilityAccounts] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [portalCredentials, setPortalCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMoreAccounts, setLoadingMoreAccounts] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [commodityFilter, setCommodityFilter] = useState('all');
  const [caStatusFilter, setCaStatusFilter] = useState('all');
  const [unitStatusFilter, setUnitStatusFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('vendors');
  
  // Utility accounts lazy loading
  const [accountsPage, setAccountsPage] = useState(1);
  const [hasMoreAccounts, setHasMoreAccounts] = useState(true);
  const [totalAccountsCount, setTotalAccountsCount] = useState(0);
  const accountsSentinelRef = useRef(null);
  const searchTimerRef = useRef(null);

  // Unit accounts state
  const [unitAccounts, setUnitAccounts] = useState([]);
  const [unitAccountsPage, setUnitAccountsPage] = useState(1);
  const [hasMoreUnitAccounts, setHasMoreUnitAccounts] = useState(true);
  const [totalUnitAccountsCount, setTotalUnitAccountsCount] = useState(0);
  const [loadingUnitAccounts, setLoadingUnitAccounts] = useState(false);
  const unitAccountsSentinelRef = useRef(null);
  
  // Modal states
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [showVendorDetailModal, setShowVendorDetailModal] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddCredentialModal, setShowAddCredentialModal] = useState(false);
  const [showEditCredentialModal, setShowEditCredentialModal] = useState(false);
  const [showDeleteCredentialConfirm, setShowDeleteCredentialConfirm] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [saving, setSaving] = useState(false);
  const [vendorDetailTab, setVendorDetailTab] = useState('details');
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  
  // Location/Space/Meter data for linking
  const [locations, setLocations] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [meters, setMeters] = useState([]);
  
  // CSV Upload states
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // New vendor form
  const [vendorForm, setVendorForm] = useState({
    vendor_name: '',
    vendor_code: '',
    email: '',
    support_phone: '',
    commodities_served: [],
    bp_supplier_id: '',
  });

  // Portal Credential form
  const [credentialForm, setCredentialForm] = useState({
    vendor_id: '',
    account_name: '',
    portal_url: '',
    username: '',
    password: '',
    account_number: '',
    bp_credential_id: '',
  });

  // Org vendor settings form
  const [orgSettingsForm, setOrgSettingsForm] = useState({
    pms_alias_name: '',
    pms_vendor_code: '',
    preferred_payment_method: '',
    ach_routing_number: '',
    ach_account_number: '',
  });

  // Account form (for add and edit)
  const [accountForm, setAccountForm] = useState({
    vendor_id: '',
    account_number: '',
    account_name: '',
    commodity: 'electricity',
    service_address: '',
    service_city: '',
    service_state: '',
    service_zip: '',
    meter_number: '',
    linked_location_id: '',
    linked_space_id: '',
    linked_meter_id: '',
    bp_entity_id: '',
    status: 'active',
    account_start: '',
    account_end: '',
    missing_bills_expected_start_date: '',
  });

  const commodities = [
    { value: 'electricity', label: 'Electricity', icon: Zap, color: 'text-yellow-600' },
    { value: 'gas', label: 'Gas', icon: Flame, color: 'text-orange-600' },
    { value: 'water', label: 'Water', icon: Droplet, color: 'text-blue-600' },
    { value: 'sewer', label: 'Sewer', icon: Droplet, color: 'text-purple-600' },
    { value: 'stormwater', label: 'Stormwater', icon: Droplet, color: 'text-teal-600' },
    { value: 'trash', label: 'Trash', icon: Wind, color: 'text-gray-700' },
    { value: 'internet', label: 'Internet', icon: Gauge, color: 'text-indigo-600' },
    { value: 'cable', label: 'Cable', icon: Gauge, color: 'text-red-600' },
    { value: 'phone', label: 'Phone', icon: Gauge, color: 'text-sky-600' },
    { value: 'ev_charger', label: 'EV Charger', icon: Zap, color: 'text-green-600' },
    { value: 'solar', label: 'Solar', icon: Sun, color: 'text-amber-500' },
    { value: 'steam', label: 'Steam', icon: Wind, color: 'text-gray-600' },
    { value: 'chilled_water', label: 'Chilled Water', icon: Snowflake, color: 'text-cyan-600' },
  ];

  useEffect(() => {
    const initializePage = async () => {
      try {
        const { data: accounts } = await api.get('/auth/accounts');
        const storedAccountStr = localStorage.getItem('current_account');
        const storedAccount = storedAccountStr ? JSON.parse(storedAccountStr) : null;
        
        let account = null;
        if (storedAccount) {
          account = accounts.find(a => a.id === storedAccount.id);
        }
        if (!account && accounts.length > 0) {
          account = accounts[0];
        }
        
        if (!account) {
          navigate('/dashboard');
          return;
        }

        if (!account.enabled_apps?.ubm) {
          navigate('/dashboard');
          return;
        }

        setCurrentAccount(account);
        localStorage.setItem('current_account', JSON.stringify(account));
      } catch (error) {
        console.error('Failed to initialize:', error);
      } finally {
        setInitializing(false);
      }
    };

    initializePage();
  }, [navigate]);

  useEffect(() => {
    if (currentAccount && !initializing) {
      fetchData();
    }
  }, [currentAccount, initializing]);

  // Org vendor settings map (vendor_id → settings)
  const [orgVendorSettingsMap, setOrgVendorSettingsMap] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vendorsRes, accountsRes, availableRes, locationsRes, spacesRes, metersRes, credentialsRes, unitAccountsRes, orgVendorSettingsRes] = await Promise.all([
        api.get(`/ubm/accounts/${currentAccount.id}/vendor-accounts`),
        api.get(`/ubm/accounts/${currentAccount.id}/utility-accounts?page=1&page_size=100`),
        api.get('/ubm/vendors'),
        api.get(`/ubm/accounts/${currentAccount.id}/locations`),
        api.get(`/ubm/accounts/${currentAccount.id}/spaces?page_size=200`),
        api.get(`/ubm/accounts/${currentAccount.id}/meters?page_size=200`),
        api.get(`/ubm/accounts/${currentAccount.id}/portal-credentials`),
        api.get(`/ubm/accounts/${currentAccount.id}/unit-accounts?page=1&page_size=100`),
        api.get(`/ubm/accounts/${currentAccount.id}/org-vendor-settings`).catch(() => ({ data: {} })),
      ]);
      
      setLocations(locationsRes.data || []);
      const spacesData = spacesRes.data?.items || spacesRes.data || [];
      setSpaces(spacesData);
      const metersData = metersRes.data?.items || metersRes.data || [];
      setMeters(metersData);
      setPortalCredentials(credentialsRes.data || []);
      
      // Store org vendor settings map
      const ovSettings = orgVendorSettingsRes.data || {};
      setOrgVendorSettingsMap(ovSettings);
      
      // Enrich vendor accounts with vendor info
      const vendorMap = {};
      (availableRes.data || []).forEach(v => { vendorMap[v.id] = v; });
      
      const enrichedVendors = (vendorsRes.data || []).map(va => ({
        ...va,
        vendor_name: vendorMap[va.vendor_id]?.vendor_name || 'Unknown',
        vendor_code: vendorMap[va.vendor_id]?.vendor_code,
        bp_supplier_id: vendorMap[va.vendor_id]?.bp_supplier_id,
      }));
      
      setVendors(enrichedVendors);
      // Handle paginated utility accounts
      const acctData = accountsRes.data;
      setUtilityAccounts(acctData.items || acctData || []);
      setTotalAccountsCount(acctData.total || (acctData.items || acctData || []).length);
      setAccountsPage(1);
      setHasMoreAccounts((acctData.total_pages || 0) > 1);
      setAvailableVendors(availableRes.data || []);

      // Handle unit accounts
      const uaData = unitAccountsRes.data;
      setUnitAccounts(uaData.items || uaData || []);
      setTotalUnitAccountsCount(uaData.total || (uaData.items || uaData || []).length);
      setUnitAccountsPage(1);
      setHasMoreUnitAccounts((uaData.total_pages || 0) > 1);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreAccounts = useCallback(async () => {
    if (!currentAccount || loadingMoreAccounts || !hasMoreAccounts) return;
    setLoadingMoreAccounts(true);
    const nextPage = accountsPage + 1;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/utility-accounts?page=${nextPage}&page_size=100`);
      const newItems = data.items || [];
      setUtilityAccounts(prev => [...prev, ...newItems]);
      setAccountsPage(nextPage);
      setHasMoreAccounts(nextPage < (data.total_pages || 0));
    } catch (error) {
      console.error('Failed to load more accounts:', error);
      setHasMoreAccounts(false);
    } finally {
      setLoadingMoreAccounts(false);
    }
  }, [currentAccount, accountsPage, loadingMoreAccounts, hasMoreAccounts]);

  const loadMoreUnitAccounts = useCallback(async () => {
    if (!currentAccount || loadingUnitAccounts || !hasMoreUnitAccounts) return;
    setLoadingUnitAccounts(true);
    const nextPage = unitAccountsPage + 1;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/unit-accounts?page=${nextPage}&page_size=100`);
      const newItems = data.items || [];
      setUnitAccounts(prev => [...prev, ...newItems]);
      setUnitAccountsPage(nextPage);
      setHasMoreUnitAccounts(nextPage < (data.total_pages || 0));
    } catch (error) {
      console.error('Failed to load more unit accounts:', error);
      setHasMoreUnitAccounts(false);
    } finally {
      setLoadingUnitAccounts(false);
    }
  }, [currentAccount, unitAccountsPage, loadingUnitAccounts, hasMoreUnitAccounts]);

  // Scroll-based lazy loading for utility accounts (works inside tabs)
  useEffect(() => {
    if (loading) return;
    const isAccounts = activeTab === 'accounts' && hasMoreAccounts && !loadingMoreAccounts;
    const isUnits = activeTab === 'unit-accounts' && hasMoreUnitAccounts && !loadingUnitAccounts;
    if (!isAccounts && !isUnits) return;
    
    const handleScroll = () => {
      const scrollBottom = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight - 300) {
        if (activeTab === 'accounts' && !loadingMoreAccounts && hasMoreAccounts) loadMoreAccounts();
        if (activeTab === 'unit-accounts' && !loadingUnitAccounts && hasMoreUnitAccounts) loadMoreUnitAccounts();
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMoreAccounts, hasMoreUnitAccounts, loading, loadingMoreAccounts, loadingUnitAccounts, activeTab, loadMoreAccounts, loadMoreUnitAccounts]);

  const handleAddVendor = async (e) => {
    e.preventDefault();
    if (!vendorForm.vendor_name.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/ubm/vendors', vendorForm);
      toast.success('Vendor added successfully');
      setShowAddVendorModal(false);
      resetVendorForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!accountForm.vendor_id || !accountForm.account_number.trim()) {
      toast.error('Vendor and account number are required');
      return;
    }
    
    setSaving(true);
    try {
      const { data } = await api.post(`/ubm/accounts/${currentAccount.id}/utility-accounts`, accountForm);
      await syncExpectedStart();
      toast.success(`Account created. Auto-linked: Property=${data.auto_linked?.location}, Space=${data.auto_linked?.space}, Meter=${data.auto_linked?.meter}`);
      setShowAddAccountModal(false);
      resetAccountForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add account');
    } finally {
      setSaving(false);
    }
  };

  const openEditAccount = (account) => {
    setEditingAccount(account);
    const toDateStr = (d) => d ? new Date(d).toISOString().split('T')[0] : '';
    setAccountForm({
      vendor_id: account.vendor_id || '',
      account_number: account.account_number || '',
      account_name: account.account_name || '',
      commodity: account.commodity || 'electricity',
      service_address: account.service_address || '',
      service_city: account.service_city || '',
      service_state: account.service_state || '',
      service_zip: account.service_zip || '',
      meter_number: account.meter_number || '',
      linked_location_id: account.linked_location_id || '',
      linked_space_id: account.linked_space_id || '',
      linked_meter_id: account.linked_meter_id || '',
      bp_entity_id: account.bp_entity_id || '',
      account_start: toDateStr(account.account_start),
      account_end: toDateStr(account.account_end),
      missing_bills_expected_start_date: toDateStr(account.missing_bills_expected_start_date),
    });
    setShowEditAccountModal(true);
  };

  // Mirror the Missing-Bills expected-start onto the flagger's source collection.
  const syncExpectedStart = async () => {
    if (!accountForm.account_number) return;
    try {
      await api.patch('/intake/missing-bills/account-settings',
        { account_kind: 'vendor', account_number: accountForm.account_number,
          start_date: accountForm.missing_bills_expected_start_date || null },
        { params: { account_id: currentAccount.id } });
    } catch { /* non-blocking */ }
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    if (!editingAccount) return;
    
    setSaving(true);
    try {
      await api.put(`/ubm/accounts/${currentAccount.id}/utility-accounts/${editingAccount.id}`, accountForm);
      await syncExpectedStart();
      toast.success('Account updated successfully');
      setShowEditAccountModal(false);
      setEditingAccount(null);
      resetAccountForm();
      fetchData();
      // Refresh vendor details if modal is open
      if (vendorDetails) {
        const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendors/${vendorDetails.vendor.id}/details`);
        setVendorDetails(data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  // Portal Credentials handlers
  const handleAddCredential = async (e) => {
    e.preventDefault();
    if (!credentialForm.vendor_id) {
      toast.error('Vendor is required');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/ubm/accounts/${currentAccount.id}/portal-credentials`, credentialForm);
      toast.success('Credential added successfully');
      setShowAddCredentialModal(false);
      resetCredentialForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add credential');
    } finally {
      setSaving(false);
    }
  };

  const openEditCredential = (credential) => {
    setSelectedCredential(credential);
    setCredentialForm({
      vendor_id: credential.vendor_id || '',
      account_name: credential.account_name || '',
      portal_url: credential.portal_url || '',
      username: credential.username || '',
      password: '', // Don't pre-fill password for security
      account_number: credential.account_number || '',
      bp_credential_id: credential.bp_credential_id || '',
    });
    setShowEditCredentialModal(true);
  };

  const handleUpdateCredential = async (e) => {
    e.preventDefault();
    if (!selectedCredential) return;
    
    setSaving(true);
    try {
      // Only send password if it was changed
      const updateData = { ...credentialForm };
      if (!updateData.password) delete updateData.password;
      
      await api.put(`/ubm/accounts/${currentAccount.id}/portal-credentials/${selectedCredential.id}`, updateData);
      toast.success('Credential updated successfully');
      setShowEditCredentialModal(false);
      setSelectedCredential(null);
      resetCredentialForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update credential');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCredential = async () => {
    if (!selectedCredential) return;
    
    setSaving(true);
    try {
      await api.delete(`/ubm/accounts/${currentAccount.id}/portal-credentials/${selectedCredential.id}`);
      toast.success('Credential deleted');
      setShowDeleteCredentialConfirm(false);
      setSelectedCredential(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete credential');
    } finally {
      setSaving(false);
    }
  };

  const resetCredentialForm = () => {
    setCredentialForm({
      vendor_id: '',
      account_name: '',
      portal_url: '',
      username: '',
      password: '',
      account_number: '',
      bp_credential_id: '',
    });
  };

  // Get filtered spaces based on selected location
  const getFilteredSpaces = () => {
    if (!accountForm.linked_location_id) return spaces;
    return spaces.filter(s => s.location_id === accountForm.linked_location_id);
  };

  // Get filtered meters based on selected location/space
  const getFilteredMeters = () => {
    if (accountForm.linked_space_id) {
      return meters.filter(m => m.space_id === accountForm.linked_space_id);
    }
    if (accountForm.linked_location_id) {
      return meters.filter(m => m.location_id === accountForm.linked_location_id);
    }
    return meters;
  };

  const openVendorDetails = async (vendorId) => {
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendors/${vendorId}/details`);
      setVendorDetails(data);
      setOrgSettingsForm({
        pms_alias_name: data.org_settings?.pms_alias_name || '',
        pms_vendor_code: data.org_settings?.pms_vendor_code || '',
        preferred_payment_method: data.org_settings?.preferred_payment_method || '',
        ach_routing_number: data.org_settings?.ach_routing_number || '',
        ach_account_number: data.org_settings?.ach_account_number || '',
        portal_url: data.org_settings?.portal_url || '',
        portal_username: data.org_settings?.portal_username || '',
        portal_password: data.org_settings?.portal_password || '',
        portal_account_number: data.org_settings?.portal_account_number || '',
      });
      setShowVendorDetailModal(true);
      setVendorDetailTab('details');
    } catch (error) {
      toast.error('Failed to load vendor details');
    }
  };

  const handleSaveOrgSettings = async () => {
    if (!vendorDetails?.vendor?.id) return;
    
    setSaving(true);
    try {
      await api.put(`/ubm/accounts/${currentAccount.id}/vendors/${vendorDetails.vendor.id}/org-settings`, orgSettingsForm);
      toast.success('Vendor settings saved');
      // Refresh details
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendors/${vendorDetails.vendor.id}/details`);
      setVendorDetails(data);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccount) return;
    
    setSaving(true);
    try {
      await api.delete(`/ubm/accounts/${currentAccount.id}/utility-accounts/${selectedAccount.id}`);
      toast.success('Account deleted');
      setShowDeleteConfirm(false);
      setSelectedAccount(null);
      fetchData();
      // Refresh vendor details if modal is open
      if (vendorDetails) {
        const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendors/${vendorDetails.vendor.id}/details`);
        setVendorDetails(data);
      }
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setSaving(false);
    }
  };

  // CSV Upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/ubm/accounts/${currentAccount.id}/utility-accounts/csv-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'utility_accounts_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await api.post(`/ubm/accounts/${currentAccount.id}/utility-accounts/csv-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResults(data);
      
      if (data.created > 0 || data.updated > 0) {
        toast.success(`Created ${data.created}, Updated ${data.updated} accounts. Auto-linked: ${data.auto_linked}. Vendors created: ${data.vendors_created}`);
        fetchData();
      }
      
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
      setUploadResults({ errors: [{ error: error.response?.data?.detail || 'Upload failed' }] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const resetVendorForm = () => {
    setVendorForm({
      vendor_name: '',
      vendor_code: '',
      email: '',
      support_phone: '',
      commodities_served: [],
      bp_supplier_id: '',
    });
  };

  const handleExportVendorAccounts = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/vendor-accounts/export`, { responseType: 'text' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendor_accounts_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportUtilityAccounts = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/utility-accounts/export`, { responseType: 'text' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `utility_accounts_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const resetAccountForm = () => {
    setAccountForm({
      vendor_id: '',
      account_number: '',
      account_name: '',
      commodity: 'electricity',
      service_address: '',
      service_city: '',
      service_state: '',
      service_zip: '',
      meter_number: '',
      linked_location_id: '',
      linked_space_id: '',
      linked_meter_id: '',
      bp_entity_id: '',
      status: 'active',
      account_start: '',
      account_end: '',
      missing_bills_expected_start_date: '',
    });
  };

  const toggleCommodity = (value) => {
    setVendorForm(prev => ({
      ...prev,
      commodities_served: prev.commodities_served.includes(value)
        ? prev.commodities_served.filter(c => c !== value)
        : [...prev.commodities_served, value]
    }));
  };

  const getCommodityBadge = (commodity) => {
    const found = commodities.find(c => c.value === commodity);
    if (!found) return <Badge variant="outline" className="rounded-none">{commodity}</Badge>;
    const Icon = found.icon;
    return (
      <Badge variant="outline" className={`rounded-none ${found.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {found.label}
      </Badge>
    );
  };

  // Filtered data
  const filteredVendors = vendors.filter(v => {
    const matchesSearch = !searchQuery || 
      v.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.account_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCommodity = commodityFilter === 'all' || v.commodity === commodityFilter;
    return matchesSearch && matchesCommodity;
  });

  const filteredAccounts = utilityAccounts.filter(a => {
    const matchesSearch = !searchQuery || 
      a.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.account_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.service_address?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCommodity = commodityFilter === 'all' || a.commodity === commodityFilter;
    const matchesStatus = caStatusFilter === 'all' || (a.status || 'active') === caStatusFilter;
    const matchesProperty = propertyFilter === 'all' || a.linked_location_id === propertyFilter;
    return matchesSearch && matchesCommodity && matchesStatus && matchesProperty;
  });

  const filteredUnitAccounts = unitAccounts.filter(a => {
    const matchesSearch = !searchQuery || 
      a.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.account_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.service_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.unit_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCommodity = commodityFilter === 'all' || a.commodity === commodityFilter;
    const matchesStatus = unitStatusFilter === 'all' || (a.occupancy_status || 'occupied') === unitStatusFilter;
    const matchesProperty = propertyFilter === 'all' || a.linked_location_id === propertyFilter;
    return matchesSearch && matchesCommodity && matchesStatus && matchesProperty;
  });

  // Group vendors by vendor_id for the Vendors tab
  const groupedVendors = {};
  vendors.forEach(va => {
    if (!groupedVendors[va.vendor_id]) {
      groupedVendors[va.vendor_id] = {
        vendor_id: va.vendor_id,
        vendor_name: va.vendor_name,
        vendor_code: va.vendor_code,
        accounts: []
      };
    }
    groupedVendors[va.vendor_id].accounts.push(va);
  });

  // Also include vendors from utility accounts
  utilityAccounts.forEach(ua => {
    if (ua.vendor_id && !groupedVendors[ua.vendor_id]) {
      const vendorInfo = availableVendors.find(v => v.id === ua.vendor_id);
      groupedVendors[ua.vendor_id] = {
        vendor_id: ua.vendor_id,
        vendor_name: ua.vendor_name || vendorInfo?.vendor_name || 'Unknown',
        vendor_code: vendorInfo?.vendor_code || null,
        bp_supplier_id: vendorInfo?.bp_supplier_id || null,
        accounts: []
      };
    }
  });

  // Merge org vendor settings into grouped vendors
  Object.keys(groupedVendors).forEach(vid => {
    if (orgVendorSettingsMap[vid]) {
      groupedVendors[vid].org_settings = orgVendorSettingsMap[vid];
      // Use created_at from org settings if available
      if (!groupedVendors[vid].created_at && orgVendorSettingsMap[vid].created_at) {
        groupedVendors[vid].created_at = orgVendorSettingsMap[vid].created_at;
      }
    }
  });

  const vendorList = Object.values(groupedVendors);

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Vendor Management</h1>
              <p className="text-sm text-muted-foreground">
                Manage vendors and utility accounts
              </p>
            </div>
            <NotesIcon pageKey="vendors" pageLabel="Vendor Management" accountId={currentAccount?.id} />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={activeTab === 'vendors' ? handleExportVendorAccounts : handleExportUtilityAccounts}
              className="rounded-none"
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(true)}
              className="rounded-none"
              data-testid="upload-accounts-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Accounts
            </Button>
            <Button
              onClick={() => setShowAddVendorModal(true)}
              className="rounded-none"
              data-testid="add-vendor-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Vendor
            </Button>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={(tab) => {
          setActiveTab(tab);
          setSearchQuery('');
          setCommodityFilter('all');
          setPropertyFilter('all');
          setCaStatusFilter('all');
          setUnitStatusFilter('all');
        }} className="w-full">
          <TabsList className="rounded-none">
            <TabsTrigger value="vendors" className="rounded-none" data-testid="vendors-tab">
              Vendors ({vendorList.length})
            </TabsTrigger>
            <TabsTrigger value="accounts" className="rounded-none" data-testid="accounts-tab">
              Utility Accounts (CA) ({totalAccountsCount})
            </TabsTrigger>
            <TabsTrigger value="unit-accounts" className="rounded-none" data-testid="unit-accounts-tab">
              Unit Accounts ({totalUnitAccountsCount})
            </TabsTrigger>
            <TabsTrigger value="credentials" className="rounded-none" data-testid="credentials-tab">
              <Key className="h-4 w-4 mr-1" />
              Portal Credentials ({portalCredentials.length})
            </TabsTrigger>
          </TabsList>

          {/* Filters */}
          <div className="flex items-center gap-4 mt-4 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by account #, supplier, address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-none"
                data-testid="search-input"
              />
            </div>
            <Select value={commodityFilter} onValueChange={setCommodityFilter}>
              <SelectTrigger className="w-40 rounded-none">
                <SelectValue placeholder="Commodity" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All Commodities</SelectItem>
                {commodities.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(activeTab === 'unit-accounts') ? (
              <Select value={unitStatusFilter} onValueChange={setUnitStatusFilter}>
                <SelectTrigger className="w-32 rounded-none" data-testid="status-filter">
                  <SelectValue placeholder="Occupancy" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="vacant">Vacant</SelectItem>
                  <SelectItem value="occupied">Occupied</SelectItem>
                </SelectContent>
              </Select>
            ) : (activeTab === 'accounts') ? (
              <Select value={caStatusFilter} onValueChange={setCaStatusFilter}>
                <SelectTrigger className="w-32 rounded-none" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            ) : null}
            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger className="w-44 rounded-none" data-testid="property-filter">
                <SelectValue placeholder="Property" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All Properties</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Vendors Tab */}
          <TabsContent value="vendors" className="mt-4">
            <div className="border rounded-none">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Vendor</th>
                    <th className="text-left p-3 font-medium">Code</th>
                    <th className="text-left p-3 font-medium">BP Supplier</th>
                    <th className="text-left p-3 font-medium">Payment Method</th>
                    <th className="text-left p-3 font-medium">Accounts</th>
                    <th className="text-left p-3 font-medium">Date Added</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {initializing || loading ? (
                    <tr>
                      <td colSpan={7} className="text-center p-8 text-muted-foreground">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : vendorList.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center p-8 text-muted-foreground">
                        <Building className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        No vendors found. Add a vendor to get started.
                      </td>
                    </tr>
                  ) : (
                    vendorList.map(vendor => (
                      <tr key={vendor.vendor_id} className="border-t hover:bg-muted/30">
                        <td className="p-3">
                          <div className="font-medium">{vendor.vendor_name}</div>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-sm">{vendor.vendor_code || '-'}</span>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-xs text-muted-foreground">{vendor.bp_supplier_id || '-'}</span>
                        </td>
                        <td className="p-3">
                          {vendor.org_settings?.preferred_payment_method ? (
                            <Badge variant="outline" className="rounded-none text-xs capitalize">
                              {vendor.org_settings.preferred_payment_method.replace('_', ' ')}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant="secondary" className="rounded-none">
                            {vendor.accounts?.length || 0} accounts
                          </Badge>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-muted-foreground">
                            {vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : (vendor.synced_at ? new Date(vendor.synced_at).toLocaleDateString() : '-')}
                          </span>
                        </td>
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openVendorDetails(vendor.vendor_id)}
                            data-testid={`view-vendor-${vendor.vendor_id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Utility Accounts Tab */}
          <TabsContent value="accounts" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddAccountModal(true)}
                className="rounded-none"
                data-testid="add-account-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Account
              </Button>
            </div>
            <div className="border rounded-none overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Vendor</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Account #</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Commodity</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Status</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Period</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Service Address</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Linked To</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {initializing || loading ? (
                      <tr>
                        <td colSpan={8} className="text-center p-8 text-muted-foreground">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : filteredAccounts.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center p-8 text-muted-foreground">
                          <Gauge className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          No utility accounts found. Upload a CSV or add an account.
                        </td>
                      </tr>
                    ) : (
                      filteredAccounts.map(account => (
                        <tr key={account.id} className="border-t hover:bg-muted/30">
                          <td className="p-3">
                            <div className="font-medium text-sm max-w-[160px] truncate" title={account.vendor_name}>{account.vendor_name}</div>
                            {account.meter_number && (
                              <div className="text-xs text-muted-foreground font-mono">Meter: {account.meter_number}</div>
                            )}
                          </td>
                          <td className="p-3 font-mono text-xs whitespace-nowrap">{account.account_number}</td>
                          <td className="p-3">{getCommodityBadge(account.commodity)}</td>
                          <td className="p-3">
                            <Badge variant={account.status === 'active' ? 'default' : 'secondary'} className="rounded-none text-xs">
                              {(account.status || 'active').charAt(0).toUpperCase() + (account.status || 'active').slice(1)}
                            </Badge>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                            {account.account_start ? new Date(account.account_start).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'}) : '–'}
                            {' – '}
                            {(() => {
                              if (!account.account_end) return 'Ongoing';
                              const endYear = new Date(account.account_end).getFullYear();
                              return endYear >= 2029 ? 'Ongoing' : new Date(account.account_end).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'});
                            })()}
                          </td>
                          <td className="p-3">
                            <div className="text-xs max-w-[200px] truncate" title={[account.service_address, account.service_city, account.service_state].filter(Boolean).join(', ')}>
                              {account.service_address || ''}
                              {(account.service_city || account.service_state) && (
                                <span className="text-muted-foreground"> {[account.service_city, account.service_state].filter(Boolean).join(', ')}</span>
                              )}
                              {!account.service_address && !account.service_city ? '-' : ''}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {account.location_name && (
                                <Badge variant="outline" className="rounded-none text-xs truncate max-w-[120px]" title={account.location_name}>
                                  {account.location_name}
                                </Badge>
                              )}
                              {!account.location_name && !account.space_name && (
                                <span className="text-muted-foreground text-xs">Not linked</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => openEditAccount(account)}
                                data-testid={`edit-account-${account.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive"
                                onClick={() => { setSelectedAccount(account); setShowDeleteConfirm(true); }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Lazy loading indicator + Load More */}
              {loadingMoreAccounts && (
                <div className="flex items-center justify-center py-3 border-t">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                  <span className="text-xs text-muted-foreground">Loading more...</span>
                </div>
              )}
              {hasMoreAccounts && !loadingMoreAccounts && (
                <div className="flex items-center justify-center py-3 border-t">
                  <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={loadMoreAccounts}>
                    Load More ({utilityAccounts.length} of {totalAccountsCount})
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-2">
              <p className="text-xs text-muted-foreground">Showing {filteredAccounts.length} of {totalAccountsCount} account(s)</p>
            </div>
          </TabsContent>

          {/* Unit Accounts Tab */}
          <TabsContent value="unit-accounts" className="mt-4">
            <div className="border rounded-none overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Supplier</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Account #</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Commodity</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Unit</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Status</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Last Bill</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Service Address</th>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap">Property</th>
                    </tr>
                  </thead>
                  <tbody>
                    {initializing || loading ? (
                      <tr>
                        <td colSpan={8} className="text-center p-8 text-muted-foreground">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : filteredUnitAccounts.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center p-8 text-muted-foreground">
                          <Building className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          No vacant unit accounts found. Run the Unit Accounts Sync from the Job Queue.
                        </td>
                      </tr>
                    ) : (
                      filteredUnitAccounts.map(account => (
                        <tr key={account.id} className="border-t hover:bg-muted/30" data-testid={`unit-account-row-${account.id}`}>
                          <td className="p-3">
                            <div className="font-medium text-sm max-w-[160px] truncate" title={account.vendor_name}>{account.vendor_name || '-'}</div>
                          </td>
                          <td className="p-3 font-mono text-xs whitespace-nowrap">{account.account_number}</td>
                          <td className="p-3">{getCommodityBadge(account.commodity)}</td>
                          <td className="p-3">
                            <span className="font-mono text-xs">{account.space_unit_number || account.unit_number || '-'}</span>
                            {account.bp_unit_id && (
                              <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[100px]" title={account.bp_unit_id}>
                                {account.bp_unit_id.slice(0, 8)}...
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge 
                              variant="outline" 
                              className={`rounded-none text-xs ${(account.occupancy_status || 'occupied') === 'vacant' ? 'border-amber-500 text-amber-700 bg-amber-50' : 'border-green-500 text-green-700 bg-green-50'}`}
                            >
                              {(account.occupancy_status || 'occupied') === 'vacant' ? 'Vacant' : 'Occupied'}
                            </Badge>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                            {account.last_bill_date ? new Date(account.last_bill_date).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'}) : '-'}
                            {account.last_bill_amount > 0 && (
                              <div className="text-[10px] font-mono">${account.last_bill_amount.toFixed(2)}</div>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="text-xs max-w-[180px] truncate" title={[account.service_address, account.service_city, account.service_state].filter(Boolean).join(', ')}>
                              {account.service_address || ''}
                              {(account.service_city || account.service_state) && (
                                <span className="text-muted-foreground"> {[account.service_city, account.service_state].filter(Boolean).join(', ')}</span>
                              )}
                              {!account.service_address && !account.service_city ? '-' : ''}
                            </div>
                          </td>
                          <td className="p-3">
                            {account.location_name ? (
                              <Badge variant="outline" className="rounded-none text-xs truncate max-w-[120px]" title={account.location_name}>
                                {account.location_name}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {loadingUnitAccounts && (
                <div className="flex items-center justify-center py-3 border-t">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                  <span className="text-xs text-muted-foreground">Loading more...</span>
                </div>
              )}
              {!loadingUnitAccounts && hasMoreUnitAccounts && unitAccounts.length > 0 && (
                <div className="flex justify-center py-3 border-t">
                  <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={loadMoreUnitAccounts}>
                    Load More ({unitAccounts.length} of {totalUnitAccountsCount})
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-2">
              <p className="text-xs text-muted-foreground">Showing {filteredUnitAccounts.length} of {totalUnitAccountsCount} unit account(s)</p>
            </div>
          </TabsContent>

          {/* Portal Credentials Tab */}
          <TabsContent value="credentials" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { resetCredentialForm(); setShowAddCredentialModal(true); }}
                className="rounded-none"
                data-testid="add-credential-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Credential
              </Button>
            </div>
            <div className="border rounded-none">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Vendor</th>
                    <th className="text-left p-3 font-medium">Account Name</th>
                    <th className="text-left p-3 font-medium">Portal URL</th>
                    <th className="text-left p-3 font-medium">Username</th>
                    <th className="text-left p-3 font-medium">Account #</th>
                    <th className="text-left p-3 font-medium">BP Cred ID</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {initializing || loading ? (
                    <tr>
                      <td colSpan={7} className="text-center p-8 text-muted-foreground">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : portalCredentials.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center p-8 text-muted-foreground">
                        <Key className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        No portal credentials found. Add credentials to access vendor portals.
                      </td>
                    </tr>
                  ) : (
                    portalCredentials.map(cred => (
                      <tr key={cred.id} className="border-t hover:bg-muted/30">
                        <td className="p-3 font-medium">{cred.vendor_name}</td>
                        <td className="p-3">{cred.account_name || '-'}</td>
                        <td className="p-3">
                          {cred.portal_url ? (
                            <a 
                              href={cred.portal_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {cred.portal_url.replace(/^https?:\/\//, '').slice(0, 30)}...
                            </a>
                          ) : '-'}
                        </td>
                        <td className="p-3 font-mono text-sm">{cred.username || '-'}</td>
                        <td className="p-3 font-mono text-sm">{cred.account_number || '-'}</td>
                        <td className="p-3">
                          <span className="font-mono text-xs text-muted-foreground">{cred.bp_credential_id || '-'}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditCredential(cred)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => { setSelectedCredential(cred); setShowDeleteCredentialConfirm(true); }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Vendor Modal */}
      <Sheet open={showAddVendorModal} onOpenChange={setShowAddVendorModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="add-vendor-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Add New Vendor</SheetTitle>
            <SheetDescription>Add a new vendor to the global registry.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleAddVendor} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Vendor Name *</Label>
                <Input className="rounded-none mt-1" value={vendorForm.vendor_name} onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })} placeholder="e.g., Pacific Gas & Electric" required data-testid="vendor-name-input" />
              </div>
              <div>
                <Label>Vendor Code</Label>
                <Input className="rounded-none mt-1 font-mono uppercase" value={vendorForm.vendor_code} onChange={(e) => setVendorForm({ ...vendorForm, vendor_code: e.target.value.toUpperCase() })} placeholder="e.g., PGE" maxLength={10} />
              </div>
              <div>
                <Label>Support Phone</Label>
                <Input className="rounded-none mt-1" value={vendorForm.support_phone} onChange={(e) => setVendorForm({ ...vendorForm, support_phone: e.target.value })} placeholder="e.g., 1-800-743-5000" />
              </div>
              <div className="col-span-2">
                <Label>Email</Label>
                <Input className="rounded-none mt-1" type="email" value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} placeholder="billing@vendor.com" />
              </div>
              <div className="col-span-2">
                <Label className="mb-2 block">Commodities Served</Label>
                <div className="flex flex-wrap gap-2">
                  {commodities.map(c => (
                    <Badge key={c.value} variant={vendorForm.commodities_served.includes(c.value) ? 'default' : 'outline'} className="rounded-none cursor-pointer px-3 py-1" onClick={() => toggleCommodity(c.value)}>{c.label}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label>BP Supplier ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={vendorForm.bp_supplier_id} onChange={(e) => setVendorForm({ ...vendorForm, bp_supplier_id: e.target.value })} placeholder="MySQL supplier ID" />
              </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowAddVendorModal(false); resetVendorForm(); }}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-add-vendor-btn">{saving ? 'Adding...' : 'Add Vendor'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Vendor Detail Modal */}
      <Dialog open={showVendorDetailModal} onOpenChange={setShowVendorDetailModal}>
        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              {vendorDetails?.vendor?.vendor_name || 'Vendor Details'}
            </DialogTitle>
          </DialogHeader>
          
          {vendorDetails && (
            <div className="space-y-6 py-4">
              {/* Vendor Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>PMS Alias Vendor Name</Label>
                  <Input
                    className="rounded-none mt-1"
                    value={orgSettingsForm.pms_alias_name}
                    onChange={(e) => setOrgSettingsForm({ ...orgSettingsForm, pms_alias_name: e.target.value })}
                    placeholder="Name used in your PMS"
                  />
                </div>
                <div>
                  <Label>PMS Vendor Code</Label>
                  <Input
                    className="rounded-none mt-1 font-mono uppercase"
                    value={orgSettingsForm.pms_vendor_code}
                    onChange={(e) => setOrgSettingsForm({ ...orgSettingsForm, pms_vendor_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., PGE001"
                  />
                </div>
                <div>
                  <Label>Preferred Payment Method</Label>
                  <Select 
                    value={orgSettingsForm.preferred_payment_method} 
                    onValueChange={(v) => setOrgSettingsForm({ ...orgSettingsForm, preferred_payment_method: v })}
                  >
                    <SelectTrigger className="rounded-none mt-1" data-testid="vendor-payment-method">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="ach">ACH</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="wire">Wire Transfer</SelectItem>
                      <SelectItem value="auto">Auto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* ACH Details — shown when ACH selected */}
              {orgSettingsForm.preferred_payment_method === 'ach' && (
                <div className="grid grid-cols-2 gap-4 p-3 border border-blue-200 bg-blue-50/30 rounded-none">
                  <div>
                    <Label>ACH Routing Number</Label>
                    <Input
                      className="rounded-none mt-1 font-mono"
                      value={orgSettingsForm.ach_routing_number || ''}
                      onChange={e => setOrgSettingsForm({ ...orgSettingsForm, ach_routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                      placeholder="9-digit routing"
                      maxLength={9}
                      data-testid="vendor-ach-routing"
                    />
                  </div>
                  <div>
                    <Label>ACH Account Number</Label>
                    <Input
                      className="rounded-none mt-1 font-mono"
                      value={orgSettingsForm.ach_account_number || ''}
                      onChange={e => setOrgSettingsForm({ ...orgSettingsForm, ach_account_number: e.target.value.replace(/\D/g, '') })}
                      placeholder="Account number"
                      data-testid="vendor-ach-account"
                    />
                  </div>
                </div>
              )}

              {/* Linked Accounts Table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">Linked Utility Accounts ({vendorDetails.linked_accounts?.length || 0})</h3>
                </div>
                <div className="border rounded-none max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Account #</th>
                        <th className="text-left p-2">Account Name</th>
                        <th className="text-left p-2">Commodity</th>
                        <th className="text-left p-2">Property</th>
                        <th className="text-left p-2">Space</th>
                        <th className="text-left p-2">Meter</th>
                        <th className="text-left p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendorDetails.linked_accounts?.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center p-4 text-muted-foreground">
                            No accounts linked to this vendor
                          </td>
                        </tr>
                      ) : (
                        vendorDetails.linked_accounts?.map(acc => (
                          <tr key={acc.id} className="border-t hover:bg-muted/30">
                            <td className="p-2 font-mono">{acc.account_number}</td>
                            <td className="p-2">{acc.account_name || '-'}</td>
                            <td className="p-2">{getCommodityBadge(acc.commodity)}</td>
                            <td className="p-2">{acc.location_name || '-'}</td>
                            <td className="p-2">{acc.space_name || '-'}</td>
                            <td className="p-2">{acc.meter_display || '-'}</td>
                            <td className="p-2">
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => { setShowVendorDetailModal(false); openEditAccount(acc); }}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-destructive"
                                  onClick={() => { setSelectedAccount(acc); setShowDeleteConfirm(true); }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-none" onClick={() => setShowVendorDetailModal(false)}>
              Close
            </Button>
            <Button className="rounded-none" onClick={handleSaveOrgSettings} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Account Side Panel */}
      <Sheet open={showAddAccountModal} onOpenChange={setShowAddAccountModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="add-account-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Add Utility Account</SheetTitle>
            <SheetDescription>Add a new utility account. It will auto-link to properties/meters if a match is found.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleAddAccount} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Vendor *</Label>
                <Select value={accountForm.vendor_id} onValueChange={(v) => setAccountForm({ ...accountForm, vendor_id: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-60">{availableVendors.map(v => (<SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.account_number} onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })} placeholder="e.g., 1234567890" required data-testid="add-account-number-input" />
              </div>
              <div>
                <Label>Account Name (Bill To)</Label>
                <Input className="rounded-none mt-1" value={accountForm.account_name} onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })} placeholder="Property or company name" data-testid="add-account-name-input" />
              </div>
              <div>
                <Label>Commodity</Label>
                <Select value={accountForm.commodity} onValueChange={(v) => setAccountForm({ ...accountForm, commodity: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{commodities.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={accountForm.status || 'active'} onValueChange={(v) => setAccountForm({ ...accountForm, status: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-account-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Start</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.account_start} onChange={(e) => setAccountForm({ ...accountForm, account_start: e.target.value })} data-testid="add-account-start" />
              </div>
              <div>
                <Label>Account End</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.account_end} onChange={(e) => setAccountForm({ ...accountForm, account_end: e.target.value })} data-testid="add-account-end" />
                <p className="text-xs text-muted-foreground mt-1">Leave blank for ongoing</p>
              </div>
              <div className="col-span-2 border-t pt-3 mt-1">
                <Label className="flex items-center gap-1">Missing Bills — Expected Start Date</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.missing_bills_expected_start_date} onChange={(e) => setAccountForm({ ...accountForm, missing_bills_expected_start_date: e.target.value })} data-testid="add-account-missing-start" />
                <p className="text-xs text-muted-foreground mt-1">When Missing-Bills tracking should begin. Defaults to the oldest bill on platform.</p>
              </div>
              <div className="col-span-2">
                <Label>Service Address</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_address} onChange={(e) => setAccountForm({ ...accountForm, service_address: e.target.value })} placeholder="123 Main St" />
              </div>
              <div>
                <Label>City</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_city} onChange={(e) => setAccountForm({ ...accountForm, service_city: e.target.value })} />
              </div>
              <div>
                <Label>State</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_state} onChange={(e) => setAccountForm({ ...accountForm, service_state: e.target.value.toUpperCase() })} maxLength={2} />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_zip} onChange={(e) => setAccountForm({ ...accountForm, service_zip: e.target.value })} />
              </div>
              <div>
                <Label>Meter Number</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.meter_number} onChange={(e) => setAccountForm({ ...accountForm, meter_number: e.target.value })} placeholder="For auto-linking" />
              </div>
              <div>
                <Label>BP Entity ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.bp_entity_id} onChange={(e) => setAccountForm({ ...accountForm, bp_entity_id: e.target.value })} placeholder="MySQL entity ID" />
              </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowAddAccountModal(false); resetAccountForm(); }}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-add-account-btn">{saving ? 'Adding...' : 'Add Account'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* CSV Upload Dialog */}
      <Dialog open={showUploadModal} onOpenChange={(open) => { setShowUploadModal(open); if (!open) setUploadResults(null); }}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Upload Utility Accounts
            </DialogTitle>
            <DialogDescription>
              Upload a CSV to bulk import accounts. Vendors will be auto-created if not found, and accounts will be auto-linked to properties/meters.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Download Template */}
            <div className="border border-dashed border-border rounded-none p-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Download Template</p>
                  <p className="text-xs text-muted-foreground">CSV with required columns</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-none" onClick={handleDownloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Template
                </Button>
              </div>
            </div>

            {/* Upload Area */}
            <div className="border border-dashed border-border rounded-none p-6 text-center bg-muted/10 hover:bg-muted/20 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="hidden"
                id="csv-upload"
                data-testid="csv-file-input"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {uploading ? 'Uploading...' : 'Click to upload CSV file'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  CSV files only
                </p>
              </label>
            </div>

            {/* Upload Results */}
            {uploadResults && (
              <div className="space-y-2">
                {(uploadResults.created > 0 || uploadResults.updated > 0) && (
                  <div className="bg-green-50 p-3 rounded-none">
                    <div className="flex items-center gap-2 text-green-600 mb-1">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">Success</span>
                    </div>
                    <div className="text-xs text-green-700 space-y-1">
                      <p>Created: {uploadResults.created}, Updated: {uploadResults.updated}</p>
                      <p>Auto-linked: {uploadResults.auto_linked} accounts</p>
                      {uploadResults.vendors_created > 0 && (
                        <p>New vendors created: {uploadResults.vendors_created} ({uploadResults.vendors_for_review} pending review)</p>
                      )}
                    </div>
                  </div>
                )}
                {uploadResults.errors?.length > 0 && (
                  <div className="bg-red-50 p-2 rounded-none">
                    <div className="flex items-center gap-2 text-red-600 mb-2">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">{uploadResults.errors.length} Error(s)</span>
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                      {uploadResults.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-red-700">
                          {err.row ? `Row ${err.row}: ` : ''}{err.error}
                        </p>
                      ))}
                      {uploadResults.errors.length > 5 && (
                        <p className="text-xs text-red-500 mt-1">
                          ...and {uploadResults.errors.length - 5} more errors
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => { setShowUploadModal(false); setUploadResults(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete account "{selectedAccount?.account_number}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" className="rounded-none" onClick={handleDeleteAccount} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Side Panel */}
      <Sheet open={showEditAccountModal} onOpenChange={(open) => { setShowEditAccountModal(open); if (!open) { setEditingAccount(null); resetAccountForm(); } }}>
        <SheetContent side="right" className="sm:max-w-[560px] w-[560px] flex flex-col p-0" data-testid="edit-account-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Edit Utility Account</SheetTitle>
            <SheetDescription>Update account details and manage property/space/meter linking.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleUpdateAccount} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Vendor *</Label>
                <Select value={accountForm.vendor_id} onValueChange={(v) => setAccountForm({ ...accountForm, vendor_id: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-60">{availableVendors.map(v => (<SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.account_number} onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })} required />
              </div>
              <div>
                <Label>Account Name (Bill To)</Label>
                <Input className="rounded-none mt-1" value={accountForm.account_name} onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })} placeholder="Property or company name" />
              </div>
              <div>
                <Label>Commodity</Label>
                <Select value={accountForm.commodity} onValueChange={(v) => setAccountForm({ ...accountForm, commodity: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{commodities.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={accountForm.status || 'active'} onValueChange={(v) => setAccountForm({ ...accountForm, status: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-account-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Start</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.account_start} onChange={(e) => setAccountForm({ ...accountForm, account_start: e.target.value })} data-testid="edit-account-start" />
              </div>
              <div>
                <Label>Account End</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.account_end} onChange={(e) => setAccountForm({ ...accountForm, account_end: e.target.value })} data-testid="edit-account-end" />
                <p className="text-xs text-muted-foreground mt-1">Leave blank for ongoing</p>
              </div>
              <div className="col-span-2 border-t pt-3 mt-1">
                <Label className="flex items-center gap-1">Missing Bills — Expected Start Date</Label>
                <Input type="date" className="rounded-none mt-1" value={accountForm.missing_bills_expected_start_date} onChange={(e) => setAccountForm({ ...accountForm, missing_bills_expected_start_date: e.target.value })} data-testid="edit-account-missing-start" />
                <p className="text-xs text-muted-foreground mt-1">When Missing-Bills tracking should begin. Defaults to the oldest bill on platform.</p>
              </div>
              <div className="col-span-2">
                <Label>Service Address</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_address} onChange={(e) => setAccountForm({ ...accountForm, service_address: e.target.value })} />
              </div>
              <div>
                <Label>City</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_city} onChange={(e) => setAccountForm({ ...accountForm, service_city: e.target.value })} />
              </div>
              <div>
                <Label>State</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_state} onChange={(e) => setAccountForm({ ...accountForm, service_state: e.target.value.toUpperCase() })} maxLength={2} />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input className="rounded-none mt-1" value={accountForm.service_zip} onChange={(e) => setAccountForm({ ...accountForm, service_zip: e.target.value })} />
              </div>
              <div>
                <Label>Meter Number (from bill)</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.meter_number} onChange={(e) => setAccountForm({ ...accountForm, meter_number: e.target.value })} />
              </div>

              {/* Linking Section */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Link className="h-4 w-4" />
                  Link to Property/Space/Meter
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label>Property</Label>
                    <Select value={accountForm.linked_location_id || 'none'} onValueChange={(v) => setAccountForm({ ...accountForm, linked_location_id: v === 'none' ? '' : v, linked_space_id: '', linked_meter_id: '' })}>
                      <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select property" /></SelectTrigger>
                      <SelectContent className="rounded-none max-h-60">
                        <SelectItem value="none">-- Not Linked --</SelectItem>
                        {locations.map(loc => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Space/Unit</Label>
                    <Select value={accountForm.linked_space_id || 'none'} onValueChange={(v) => setAccountForm({ ...accountForm, linked_space_id: v === 'none' ? '' : v })} disabled={!accountForm.linked_location_id}>
                      <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select space" /></SelectTrigger>
                      <SelectContent className="rounded-none max-h-60">
                        <SelectItem value="none">-- Not Linked --</SelectItem>
                        {getFilteredSpaces().map(space => (<SelectItem key={space.id} value={space.id}>{space.unit_number || space.address || `Space ${space.id.slice(0,8)}`}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Meter</Label>
                    <Select value={accountForm.linked_meter_id || 'none'} onValueChange={(v) => setAccountForm({ ...accountForm, linked_meter_id: v === 'none' ? '' : v })} disabled={!accountForm.linked_location_id}>
                      <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select meter" /></SelectTrigger>
                      <SelectContent className="rounded-none max-h-60">
                        <SelectItem value="none">-- Not Linked --</SelectItem>
                        {getFilteredMeters().map(meter => (<SelectItem key={meter.id} value={meter.id}>{meter.meter_number} ({meter.commodity})</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div>
                <Label>BP Entity ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={accountForm.bp_entity_id} onChange={(e) => setAccountForm({ ...accountForm, bp_entity_id: e.target.value })} />
              </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowEditAccountModal(false); setEditingAccount(null); resetAccountForm(); }}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-edit-account-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Add Credential Modal */}
      <Dialog open={showAddCredentialModal} onOpenChange={setShowAddCredentialModal}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Portal Credential</DialogTitle>
            <DialogDescription>
              Add login credentials for a vendor portal. Credentials are stored securely for this organization only.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCredential}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Vendor *</Label>
                <Select value={credentialForm.vendor_id} onValueChange={(v) => setCredentialForm({ ...credentialForm, vendor_id: v })}>
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-60">
                    {availableVendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Account Name</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.account_name}
                  onChange={(e) => setCredentialForm({ ...credentialForm, account_name: e.target.value })}
                  placeholder="e.g., Main Office or Company ABC"
                />
              </div>
              <div className="col-span-2">
                <Label>Portal URL</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.portal_url}
                  onChange={(e) => setCredentialForm({ ...credentialForm, portal_url: e.target.value })}
                  placeholder="https://vendor-portal.com/login"
                />
              </div>
              <div>
                <Label>Username</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.username}
                  onChange={(e) => setCredentialForm({ ...credentialForm, username: e.target.value })}
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  className="rounded-none mt-1"
                  type="password"
                  value={credentialForm.password}
                  onChange={(e) => setCredentialForm({ ...credentialForm, password: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Account Number</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.account_number}
                  onChange={(e) => setCredentialForm({ ...credentialForm, account_number: e.target.value })}
                  placeholder="Account number used to login"
                />
              </div>
              <div>
                <Label>BP Credential ID</Label>
                <Input
                  className="rounded-none mt-1 font-mono"
                  value={credentialForm.bp_credential_id}
                  onChange={(e) => setCredentialForm({ ...credentialForm, bp_credential_id: e.target.value })}
                  placeholder="BillPortal reference"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowAddCredentialModal(false); resetCredentialForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-none" disabled={saving}>
                {saving ? 'Adding...' : 'Add Credential'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Credential Modal */}
      <Dialog open={showEditCredentialModal} onOpenChange={(open) => { setShowEditCredentialModal(open); if (!open) { setSelectedCredential(null); resetCredentialForm(); } }}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Portal Credential</DialogTitle>
            <DialogDescription>
              Update login credentials. Leave password blank to keep the existing password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCredential}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Vendor *</Label>
                <Select value={credentialForm.vendor_id} onValueChange={(v) => setCredentialForm({ ...credentialForm, vendor_id: v })}>
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-60">
                    {availableVendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Account Name</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.account_name}
                  onChange={(e) => setCredentialForm({ ...credentialForm, account_name: e.target.value })}
                  placeholder="e.g., Main Office or Company ABC"
                />
              </div>
              <div className="col-span-2">
                <Label>Portal URL</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.portal_url}
                  onChange={(e) => setCredentialForm({ ...credentialForm, portal_url: e.target.value })}
                />
              </div>
              <div>
                <Label>Username</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.username}
                  onChange={(e) => setCredentialForm({ ...credentialForm, username: e.target.value })}
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  className="rounded-none mt-1"
                  type="password"
                  value={credentialForm.password}
                  onChange={(e) => setCredentialForm({ ...credentialForm, password: e.target.value })}
                  placeholder="Leave blank to keep existing"
                />
              </div>
              <div className="col-span-2">
                <Label>Account Number</Label>
                <Input
                  className="rounded-none mt-1"
                  value={credentialForm.account_number}
                  onChange={(e) => setCredentialForm({ ...credentialForm, account_number: e.target.value })}
                />
              </div>
              <div>
                <Label>BP Credential ID</Label>
                <Input
                  className="rounded-none mt-1 font-mono"
                  value={credentialForm.bp_credential_id}
                  onChange={(e) => setCredentialForm({ ...credentialForm, bp_credential_id: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowEditCredentialModal(false); setSelectedCredential(null); resetCredentialForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-none" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Credential Confirmation */}
      <Dialog open={showDeleteCredentialConfirm} onOpenChange={setShowDeleteCredentialConfirm}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Delete Credential</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this portal credential? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowDeleteCredentialConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" className="rounded-none" onClick={handleDeleteCredential} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default VendorAccounts;
